import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import type { IBoundingboxViewer3dItem } from "../RoomSectionViews/types";
import { createBoundingBoxMeshes } from "./geometry";
import { WalkCameraController } from "./WalkCameraController";
import { StreetViewHelp, shouldShowStreetViewHelp, rememberStreetViewHelp } from "./StreetViewHelp";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { createRenderItemsKey, getInitialCameraZ } from "./sceneState";


// Positions, box extents, eye height and walk speed use meters.
const EYE_HEIGHT = 1.65;
const WALK_SPEED = 5;
const CAMERA_PUBLISH_INTERVAL_MS = 10;

export type BoundingBoxViewer3dViewMode = "orbit" | "walk";

export type BoundingBoxViewer3dProps = {
  boundingBoxViewer3dItems: IBoundingboxViewer3dItem[];
  initialCameraPosition: [number, number, number];
  viewMode?: BoundingBoxViewer3dViewMode;
  locationLabel?: string;
  floorName?: string;
  initialCameraZ?: number;
  onCameraPositionChange?: (cameraPosition: [number, number, number]) => void;
  onCameraDirectionChange?: (cameraDirection: [number, number, number]) => void;
  /**
   * Optional external horizontal look direction, in the same world/model frame
   * the 3D camera lives in. When it changes the camera will turn to face it
   * (unless the user is actively looking around via pointer lock).
   */
  externalLookDirection?: [number, number, number] | null;
  /** Called with the underlying item when the user clicks (not drags) on a box. */
  onItemClick?: (item: IBoundingboxViewer3dItem["item"] | null) => void;
};




const BoundingBoxViewer3d: React.FC<BoundingBoxViewer3dProps> = ({
  boundingBoxViewer3dItems,
  initialCameraPosition,
  viewMode = "walk",
  locationLabel,
  floorName,
  initialCameraZ,
  onCameraPositionChange,
  onCameraDirectionChange,
  externalLookDirection,
  onItemClick,
}) => {
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const worldOriginRef = useRef(new THREE.Vector3());
  const hasWorldOriginRef = useRef(false);
  const mountRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<THREE.Object3D[] | null>(null);
  const onItemClickRef = useRef<((item: IBoundingboxViewer3dItem["item"] | null) => void) | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controllerRef = useRef<WalkCameraController | null>(null);
  const orbitControlsRef = useRef<OrbitControls | null>(null);
  const lastGeometryRef = useRef<string | null>(null);
  const lastRenderItemsKeyRef = useRef<string | null>(null);
  const onCameraPositionChangeRef = useRef(onCameraPositionChange);
  const cameraDirectionChangeRef = useRef(onCameraDirectionChange);
  const selectedItemRef = useRef<THREE.Vector3 | null>(null);
  // Mirror of viewMode readable from effects that must not re-run on mode change.
  const viewModeRef = useRef<"orbit" | "walk">("walk");
  const [helpOpen, setHelpOpen] = useState(shouldShowStreetViewHelp);
  const [fps, setFps] = useState<number | null>(null);

  useEffect(() => {
    onCameraPositionChangeRef.current = onCameraPositionChange;
    cameraDirectionChangeRef.current = onCameraDirectionChange;
    onItemClickRef.current = onItemClick ?? null;
  }, [onCameraPositionChange, onCameraDirectionChange, onItemClick]);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const SCREEN_WIDTH = container.clientWidth || 400;
    const SCREEN_HEIGHT = container.clientHeight || 300;

    const renderer = new THREE.WebGLRenderer({
      antialias: false,
      logarithmicDepthBuffer: true,
      powerPreference: "high-performance",
    });
    // Cap pixel ratio so 2x/3x displays don't render 4x-9x the pixel work.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    rendererRef.current = renderer;
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    // A screen-space gradient keeps the backdrop calm in both walk and orbit.
    const backgroundCanvas = document.createElement("canvas");
    backgroundCanvas.width = 2;
    backgroundCanvas.height = 256;
    const backgroundContext = backgroundCanvas.getContext("2d");
    let backgroundTexture: THREE.CanvasTexture | undefined;
    scene.background = new THREE.Color(0xe1e8ed);
    if (backgroundContext) {
      const gradient = backgroundContext.createLinearGradient(0, 0, 0, 256);
      gradient.addColorStop(0, "#a7bbc9");
      gradient.addColorStop(0.55, "#e1e8ed");
      gradient.addColorStop(1, "#f7f6f2");
      backgroundContext.fillStyle = gradient;
      backgroundContext.fillRect(0, 0, 2, 256);
      backgroundTexture = new THREE.CanvasTexture(backgroundCanvas);
      backgroundTexture.colorSpace = THREE.SRGBColorSpace;
      backgroundTexture.minFilter = THREE.LinearFilter;
      backgroundTexture.generateMipmaps = false;
      scene.background = backgroundTexture;
    }

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.0);
    dirLight.position.set(1, 1, 3);
    scene.add(dirLight);

    const aspect = SCREEN_WIDTH / SCREEN_HEIGHT;
    const camera = new THREE.PerspectiveCamera(75, aspect, 1, 1_000_000);
    cameraRef.current = camera;
    camera.position.set(0, 0, EYE_HEIGHT);

    container.appendChild(renderer.domElement);
    renderer.setClearColor(0x111111, 1);

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        renderer.setSize(width, height);
      }
    });
    observer.observe(container);

    // --- Single-click selection (works in both orbit and walk modes) ---
    // Walk mode requests pointer lock on pointerdown, which freezes clientX/Y,
    // so we save the pre-lock position and raycast on pointerup instead of
    // using the native click event.
    const CLICK_DRAG_THRESHOLD = 5;
    const clickRaycaster = new THREE.Raycaster();
    const clickNdc = new THREE.Vector2();
    let clickDownPos: { x: number; y: number } | null = null;
    let clickAccMovX = 0;
    let clickAccMovY = 0;
    const resetSelectionGesture = () => { clickDownPos = null; };

    const onSelectPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) { resetSelectionGesture(); return; }
      clickDownPos = { x: e.clientX, y: e.clientY };
      clickAccMovX = 0;
      clickAccMovY = 0;
    };
    const onSelectPointerMove = (e: PointerEvent) => {
      if (!clickDownPos) return;
      if (!(e.buttons & 1)) return;
      clickAccMovX += Math.abs(e.movementX);
      clickAccMovY += Math.abs(e.movementY);
    };
    const onSelectPointerUp = (e: PointerEvent) => {

      if (e.button !== 0) return;
      const downPos = clickDownPos;
      clickDownPos = null;
      if (!downPos) return;
      // Accumulate the whole gesture, so dragging away and back is still a drag.
      // Relative movement also works while walk mode holds pointer lock.
      if (Math.hypot(clickAccMovX, clickAccMovY) > CLICK_DRAG_THRESHOLD ||
        Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y) > CLICK_DRAG_THRESHOLD) return;
      const rect = renderer.domElement.getBoundingClientRect();
      clickNdc.x = ((downPos.x - rect.left) / rect.width) * 2 - 1;
      clickNdc.y = -((downPos.y - rect.top) / rect.height) * 2 + 1;
      clickRaycaster.setFromCamera(clickNdc, camera);

      const targets = itemRef.current ?? [];
      if (targets.length === 0) return;
      const hits = clickRaycaster.intersectObjects(targets, false);
      // Skip outline LineSegments (they don't carry userData.item).
      const hit = hits.find((h) => h.object.userData?.item || h.object.userData?.items);
      const item = (hit?.object.userData.item ??
        (typeof hit?.instanceId === "number"
          ? hit.object.userData.items?.[hit.instanceId]
          : undefined)) as
        | IBoundingboxViewer3dItem["item"]
        | undefined;
      onItemClickRef.current?.(item ?? null);

    };

    renderer.domElement.addEventListener("pointerdown", onSelectPointerDown);
    renderer.domElement.addEventListener("pointermove", onSelectPointerMove);
    renderer.domElement.addEventListener("pointerup", onSelectPointerUp);
    renderer.domElement.addEventListener("pointercancel", resetSelectionGesture);

    // Walk controller and orbit controls are created/swapped by the viewMode
    // effect below; the animate loop just reads whichever is currently active.

    let lastTime = performance.now();
    let prevX = Number.NaN;
    let prevY = Number.NaN;
    let prevZ = Number.NaN;
    // Change detection so the direction callback doesn't fire every frame.
    // Without this, external inputs (e.g. 2D cone drag) get overwritten by
    // the parent state re-syncing to the 3D camera on every animation tick.
    let prevDirX = Number.NaN;
    let prevDirY = Number.NaN;
    let prevDirZ = Number.NaN;
    let lastCameraPublishAt = 0;
    let fpsFrameCount = 0;
    let lastFpsPublishAt = performance.now();
    const cameraDirection = new THREE.Vector3();

    // OrbitControls.update() roundtrips camera.position through spherical
    // coords every frame, so even at rest the values jitter at float noise
    // level. Without a tolerance, that fires the change callbacks on every
    // frame in orbit mode and causes a re-render loop up in MultiViewer.
    const POS_EPS = 1e-3;
    const DIR_EPS = 1e-4;

    function animate() {
      const now = performance.now();
      const dt = Math.min(0.1, (now - lastTime) / 1000);
      lastTime = now;
      fpsFrameCount += 1;

      if (now - lastFpsPublishAt >= 500) {
        setFps((fpsFrameCount * 1000) / (now - lastFpsPublishAt));
        fpsFrameCount = 0;
        lastFpsPublishAt = now;
      }
      
      if (selectedItemRef.current && orbitControlsRef.current) {
        orbitControlsRef.current.target.lerp(selectedItemRef.current, 0.1);
      }

      controllerRef.current?.update(dt);
      orbitControlsRef.current?.update();
      const shouldPublishCamera = now - lastCameraPublishAt >= CAMERA_PUBLISH_INTERVAL_MS;
      if (shouldPublishCamera) {
        lastCameraPublishAt = now;
      }
      const cb = onCameraPositionChangeRef.current;
      if (cb && shouldPublishCamera) {
        const { x, y, z } = camera.position;
        const origin = worldOriginRef.current;
        if (
          !(Math.abs(x - prevX) < POS_EPS) ||
          !(Math.abs(y - prevY) < POS_EPS) ||
          !(Math.abs(z - prevZ) < POS_EPS)
        ) {
          prevX = x;
          prevY = y;
          prevZ = z;
          cb([x + origin.x, y + origin.y, z + origin.z]);
        }
      }

      const cdirCb = cameraDirectionChangeRef.current;
      // Keep the 2D cone in step with the 3D camera even while the user is
      // actively dragging to look around. The existing epsilon guard avoids
      // noisy frame-to-frame re-renders without freezing the live direction.
      if (cdirCb && shouldPublishCamera) {
        const cdir = camera.getWorldDirection(cameraDirection);
        if (
          !(Math.abs(cdir.x - prevDirX) < DIR_EPS) ||
          !(Math.abs(cdir.y - prevDirY) < DIR_EPS) ||
          !(Math.abs(cdir.z - prevDirZ) < DIR_EPS)
        ) {
          prevDirX = cdir.x;
          prevDirY = cdir.y;
          prevDirZ = cdir.z;
          cdirCb([cdir.x, cdir.y, cdir.z]);
        }
      }

      renderer.render(scene, camera);
    }
    renderer.setAnimationLoop(animate);

    return () => {
      renderer.setAnimationLoop(null);
      renderer.domElement.removeEventListener("pointerdown", onSelectPointerDown);
      renderer.domElement.removeEventListener("pointermove", onSelectPointerMove);
      renderer.domElement.removeEventListener("pointerup", onSelectPointerUp);
      renderer.domElement.removeEventListener("pointercancel", resetSelectionGesture);
      controllerRef.current?.dispose();
      controllerRef.current = null;
      orbitControlsRef.current?.dispose();
      orbitControlsRef.current = null;
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      backgroundTexture?.dispose();
      itemRef.current?.forEach((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
        }
      });
    };
  }, []);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  useEffect(() => {
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    if (!camera || !renderer) return;

    if (viewMode === "walk") {
      orbitControlsRef.current?.dispose();
      orbitControlsRef.current = null;
      controllerRef.current?.dispose();
      controllerRef.current = new WalkCameraController(camera, renderer.domElement, {
        walkSpeed: WALK_SPEED,
      });
    } else {
      controllerRef.current?.dispose();
      controllerRef.current = null;
      orbitControlsRef.current?.dispose();

      const orbitControls = new OrbitControls(camera, renderer.domElement);
      // Orbit around a point in front of the camera along its current look dir
      // so there is real distance between camera and target.
      const lookDir = camera.getWorldDirection(new THREE.Vector3());
      orbitControls.target.copy(camera.position).addScaledVector(lookDir, 10);
      orbitControls.enableDamping = true;
      orbitControls.dampingFactor = 0.15;
      // >>> RAYCAST PIVOT: make wheel dolly toward the cursor as well.
      orbitControls.zoomToCursor = true;
      // Scale positions span thousands while box sizes are meters, so a full
      // wheel step at default speed dollies a huge distance. Tame it.
      //orbitControls.zoomSpeed = 0.5;
      orbitControls.update();
      orbitControlsRef.current = orbitControls;

      // >>> RAYCAST PIVOT: reusable raycaster + NDC vector.
      const raycaster = new THREE.Raycaster();
      const ndc = new THREE.Vector2();

      // >>> RAYCAST PIVOT: on pointerdown, raycast from the cursor to find how
      // FAR the clicked object is, then place the orbit target along the
      // camera's current forward direction at that distance. We deliberately
      // do NOT place the target at the raycast hit point directly: OrbitControls
      // calls camera.lookAt(target) in update(), so an off-axis target would
      // snap the camera orientation and (with damping) cause a visible flicker
      // on click. Placing it along the current forward ray preserves the
      // camera pose while still giving the orbit the right pivot depth.
      const setPivotFromCursor = (event: PointerEvent) => {
        const controls = orbitControlsRef.current;
        if (!controls) return;

        const rect = renderer.domElement.getBoundingClientRect();
        ndc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        ndc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        raycaster.setFromCamera(ndc, camera);

        const targets = itemRef.current ?? [];
        const hits = targets.length > 0 ? raycaster.intersectObjects(targets, false) : [];

        // Pick a pivot distance: raycast hit distance, or keep the current one.
        const pivotDistance = hits.length > 0
          ? camera.position.distanceTo(hits[0].point)
          : camera.position.distanceTo(controls.target);

        // Place target along current forward ray → no orientation change.
        const forward = camera.getWorldDirection(new THREE.Vector3());
        controls.target.copy(camera.position).addScaledVector(forward, pivotDistance);
        controls.update();
      };

      // >>> RAYCAST PIVOT: attach listener; cleanup happens in the branch's
      // dispose path below (via the returned cleanup function).
      const dom = renderer.domElement;
      dom.addEventListener("pointerdown", setPivotFromCursor);

      // Wheel zoom stops when the camera reaches the target (minDistance = 0).
      // If the user is zooming in and there's little room left, push the
      // target further along the forward ray so they can keep dollying.
      const ZOOM_MIN_LEEWAY = 5;
      const ZOOM_PUSH_DISTANCE = 20;
      const onWheel = (event: WheelEvent) => {
        const controls = orbitControlsRef.current;
        if (!controls || event.deltaY >= 0) return;
        if (camera.position.distanceTo(controls.target) >= ZOOM_MIN_LEEWAY) return;
        const forward = camera.getWorldDirection(new THREE.Vector3());
        controls.target.copy(camera.position).addScaledVector(forward, ZOOM_PUSH_DISTANCE);
        controls.update();
      };
      dom.addEventListener("wheel", onWheel, { passive: true, capture: true });

      return () => {
        dom.removeEventListener("pointerdown", setPivotFromCursor);
        dom.removeEventListener("wheel", onWheel, { capture: true } as AddEventListenerOptions);
      };
    }
  }, [viewMode]);

  useEffect(() => {
    controllerRef.current?.setEnabled(!helpOpen);
  }, [helpOpen, viewMode]);

  useEffect(() => {
    const control = orbitControlsRef.current;
    //console.log("testUseEffect");
    boundingBoxViewer3dItems.forEach((item) => {
      if (item.color === "#dc4242") {
        const center = item.item.geometry?.Obb3DWorldMmList?.[0]?.centerM;
        const origin = worldOriginRef.current;
        selectedItemRef.current = center
          ? new THREE.Vector3(center.x - origin.x, center.y - origin.y, center.z - origin.z)
          : null;
        // control?.target.set(center?.x ?? 0, center?.y ?? 0, center?.z ?? 0);
        // control?.update();
      }
    });
    if (!control) return;

  }, [boundingBoxViewer3dItems]);

  // Set camera XY from initialCameraPosition (Z is handled by bbox effect below).
  // Parent creates a new array literal each render, so this effect runs on
  // every re-render. Guard against the feedback echo (parent syncing state
  // from our own position callback) so we don't fight OrbitControls each frame.
  useEffect(() => {
    const camera = cameraRef.current;
    if (!camera) return;
    const [nx, ny] = initialCameraPosition;
    const origin = worldOriginRef.current;
    if (
      Math.abs(camera.position.x - (nx - origin.x)) < 1e-3 &&
      Math.abs(camera.position.y - (ny - origin.y)) < 1e-3
    ) {
      return;
    }
    camera.position.x = nx - origin.x;
    camera.position.y = ny - origin.y;
  }, [initialCameraPosition]);

  // Apply an updated camera setting even if the geometry is unchanged (for
  // example when it arrives asynchronously), and restore it when entering Walk.
  useEffect(() => {
    const camera = cameraRef.current;
    if (!camera || !hasWorldOriginRef.current || viewMode !== "walk" ||
        initialCameraZ == null || !Number.isFinite(initialCameraZ)) return;
    camera.position.z = initialCameraZ - worldOriginRef.current.z;
  }, [initialCameraZ, viewMode]);

  // Apply an externally-driven horizontal look direction (e.g. cone rotation
  // from the 2D viewer). Routes into whichever controller is active. Both
  // paths guard against feedback with the direction reported back every frame.
  useEffect(() => {
    if (!externalLookDirection) return;
    const [x, y, z] = externalLookDirection;

    const walk = controllerRef.current;
    if (walk) {
      walk.setLookDirection(new THREE.Vector3(x, y, z));
      return;
    }

    const orbit = orbitControlsRef.current;
    const camera = cameraRef.current;
    if (!orbit || !camera) return;

    // Rotate the orbit target around the camera's vertical axis so its
    // horizontal forward matches (x, y). Preserves camera position, the
    // horizontal distance to target, and the vertical offset (elevation).
    const lenXY = Math.hypot(x, y);
    if (lenXY < 1e-6) return;
    const nx = x / lenXY;
    const ny = y / lenXY;
    const offX = orbit.target.x - camera.position.x;
    const offY = orbit.target.y - camera.position.y;
    const offZ = orbit.target.z - camera.position.z;
    const horizDist = Math.hypot(offX, offY);
    if (horizDist < 1e-6) return;

    // Skip if already aligned (avoids feedback loop with the frame callback).
    const curNx = offX / horizDist;
    const curNy = offY / horizDist;
    if (Math.abs(curNx - nx) < 1e-4 && Math.abs(curNy - ny) < 1e-4) return;

    orbit.target.set(
      camera.position.x + nx * horizDist,
      camera.position.y + ny * horizDist,
      camera.position.z + offZ,
    );
    orbit.update();
  }, [externalLookDirection]);

  // Draw a red edge outline around the currently-selected item, built directly
  // from its OBBs. Rebuilt on selection change; independent of the main mesh
  // pipeline so scene refetches don't clobber it.
  // useEffect(() => {
  //   const scene = sceneRef.current;
  //   if (!scene) return;

  //   const prev = selectionOutlineRef.current;
  //   if (prev) {
  //     scene.remove(prev);
  //     prev.traverse((obj) => {
  //       if (obj instanceof THREE.LineSegments) {
  //         obj.geometry.dispose();
  //         (obj.material as THREE.Material).dispose();
  //       }
  //     });
  //     selectionOutlineRef.current = null;
  //   }

  //   if (!selectedItem) return;
  //   const obbs = selectedItem.geometry?.Obb3DWorldMmList ?? [];
  //   if (obbs.length === 0) return;

  //   const group = new THREE.Group();
  //   for (const obb of obbs) {
  //     const {
  //       centerMm: c,
  //       axisA,
  //       axisB,
  //       axisC,
  //       extentAMm: eA,
  //       extentBMm: eB,
  //       extentCMm: eC,
  //     } = obb;

  //     const box = new THREE.BoxGeometry(
  //       (2 * eA) / 1000,
  //       (2 * eB) / 1000,
  //       (2 * eC) / 1000,
  //     );
  //     const edges = new THREE.EdgesGeometry(box);
  //     box.dispose();

  //     const material = new THREE.LineBasicMaterial({
  //       color: 0xff0000,
  //       depthTest: false,
  //       depthWrite: false,
  //       // Transparent queue renders after the opaque queue, so this keeps
  //       // the outline on top of the (transparent) box meshes.
  //       transparent: true,
  //     });
  //     const segments = new THREE.LineSegments(edges, material);
  //     segments.position.set(c.x, c.y, c.z);
  //     segments.quaternion.setFromRotationMatrix(
  //       new THREE.Matrix4().set(
  //         axisA.x, axisB.x, axisC.x, 0,
  //         axisA.y, axisB.y, axisC.y, 0,
  //         axisA.z, axisB.z, axisC.z, 0,
  //         0,       0,       0,       1,
  //       ),
  //     );
  //     segments.renderOrder = 999;
  //     group.add(segments);
  //   }

  //   scene.add(group);
  //   selectionOutlineRef.current = group;

  //   return () => {
  //     scene.remove(group);
  //     group.traverse((obj) => {
  //       if (obj instanceof THREE.LineSegments) {
  //         obj.geometry.dispose();
  //         (obj.material as THREE.Material).dispose();
  //       }
  //     });
  //     if (selectionOutlineRef.current === group) {
  //       selectionOutlineRef.current = null;
  //     }
  //   };
  // }, [selectedItem]);

  useEffect(() => {
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    if (!scene || !camera) return;

    const renderItemsKey = createRenderItemsKey(boundingBoxViewer3dItems);
    if (renderItemsKey === lastRenderItemsKeyRef.current) return;
    lastRenderItemsKeyRef.current = renderItemsKey;

    // remove previous
    itemRef.current?.forEach((obj) => {
      scene.remove(obj);
      if (obj instanceof THREE.Mesh || obj instanceof THREE.LineSegments) {
        obj.geometry.dispose();
        (obj.material as THREE.Material).dispose();
      }
    });
    itemRef.current = null;

    if (boundingBoxViewer3dItems.length === 0) {
      lastGeometryRef.current = null;
      return;
    }

    // Compute the visible-space AABB.
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;
    let minZ = Infinity,
      maxZ = -Infinity;
    let hasObb = false;
    for (const { item } of boundingBoxViewer3dItems) {
      const obbs = item.geometry?.Obb3DWorldMmList ?? [];
      for (const obb of obbs) {
        hasObb = true;
        const { centerM: c, axisA, axisB, axisC } = obb;
        const extentA = obb.extentAM;
        const extentB = obb.extentBM;
        const extentC = obb.extentCM;
        const hx = Math.abs(axisA.x) * extentA + Math.abs(axisB.x) * extentB + Math.abs(axisC.x) * extentC;
        const hy = Math.abs(axisA.y) * extentA + Math.abs(axisB.y) * extentB + Math.abs(axisC.y) * extentC;
        const hz = Math.abs(axisA.z) * extentA + Math.abs(axisB.z) * extentB + Math.abs(axisC.z) * extentC;
        if (c.x - hx < minX) minX = c.x - hx;
        if (c.x + hx > maxX) maxX = c.x + hx;
        if (c.y - hy < minY) minY = c.y - hy;
        if (c.y + hy > maxY) maxY = c.y + hy;
        if (c.z - hz < minZ) minZ = c.z - hz;
        if (c.z + hz > maxZ) maxZ = c.z + hz;
      }
    }

    if (hasObb) {
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      const sceneSize = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 1);

      if (!hasWorldOriginRef.current) {
        worldOriginRef.current.set(cx, cy, (minZ + maxZ) / 2);
        hasWorldOriginRef.current = true;
      }
      const origin = worldOriginRef.current;
      const selectedItem = boundingBoxViewer3dItems.find((item) => item.color === "#dc4242");
      const selectedCenter = selectedItem?.item.geometry?.Obb3DWorldMmList?.[0]?.centerM;
      selectedItemRef.current = selectedCenter
        ? new THREE.Vector3(
            selectedCenter.x - origin.x,
            selectedCenter.y - origin.y,
            selectedCenter.z - origin.z,
          )
        : null;
      const objs = createBoundingBoxMeshes(boundingBoxViewer3dItems, origin);
      itemRef.current = objs;
      objs.forEach((o) => scene.add(o));

      const geometry = boundingBoxViewer3dItems
        .map((i) => i.item.globalId ?? i.item._id ?? "")
        .join("|");
      if (geometry === lastGeometryRef.current) return;
      const isFirstLoad = lastGeometryRef.current === null;
      lastGeometryRef.current = geometry;

      // In orbit mode we deliberately leave the camera pose and orbit target
      // alone so a geometry refetch doesn't yank the user out of their view.
      if (viewModeRef.current === "orbit") return;

      const preservedDir = new THREE.Vector3();
      camera.getWorldDirection(preservedDir);
      const walkController = controllerRef.current;

      if (isFirstLoad) {
        // Only snap Z on first load; later refetches must preserve the user's
        // walk height or the camera can drop under the floor when new boxes
        // (e.g. basement) extend minZ downward.
        camera.position.x = initialCameraPosition[0] - origin.x;
        camera.position.y = initialCameraPosition[1] - origin.y;
        camera.position.z = getInitialCameraZ(initialCameraZ, minZ) - origin.z;
        walkController?.setLookTarget(new THREE.Vector3(cx - origin.x, cy - origin.y, camera.position.z));
      } else {
        walkController?.setLookDirection(preservedDir);
      }

      //walkController?.setLookTarget(new THREE.Vector3(cx, cy, camera.position.z));
      const orbitControls = orbitControlsRef.current;
      if (orbitControls) {

        orbitControls.target.set(cx - origin.x, cy - origin.y, (minZ + maxZ) / 2 - origin.z);
        orbitControls.update();

        // boundingBoxViewer3dItems.forEach((item, index) => {
        //   if (item.color === "#dc4242") {
        //     const center = item.item.geometry?.Obb3DWorldMmList?.[0]?.centerM;

        //     if (center) {

        //       orbitControls.target.set(center.x, center.y, center.z);
        //     }
        //   }
        // });
      }
    }
  }, [boundingBoxViewer3dItems]);

  return (
    <div
      ref={mountRef}
      style={{
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
        padding: 1,
        overflow: "hidden",
        background: "#f8f9fa",
        color: "#212529",
        position: "relative",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {viewMode === "walk" && locationLabel && (
        <div className="streetview-location" role="status" aria-live="polite" aria-atomic="true">
          <span className="streetview-location-caption">DU ER HER</span>
          <strong>{locationLabel}</strong>
          {floorName && <span>Etasje: {floorName}</span>}
        </div>
      )}
      {viewMode === "walk" && <StreetViewHelp open={helpOpen}
        onOpen={() => setHelpOpen(true)}
        onClose={() => { rememberStreetViewHelp(); setHelpOpen(false); }} />}

      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          pointerEvents: "none",
          userSelect: "none",
          padding: "3px 6px",
          borderRadius: 4,
          background: "rgba(17, 24, 39, 0.72)",
          color: "rgba(255,255,255,0.92)",
          fontSize: 11,
          lineHeight: 1.3,
          fontFamily: "inherit",
        }}
      >
        FPS: {fps == null ? "--" : fps.toFixed(0)}
      </div>
    </div>
  );
};

export default React.memo(BoundingBoxViewer3d);
